import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Icon,
  Spinner,
  Button,
  Tooltip,
  Badge,
} from "@chakra-ui/react";
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaMinusCircle, FaSyncAlt, FaRegClock } from "react-icons/fa";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";

const COLUMN_LABELS = {
  sales: "Sales",
  sdc: "SDC",
  strikerate: "Strike Rate",
  dc: "DC",
  articles: "Articles",
};

const STATE_META = {
  ok: { icon: FaCheckCircle, color: "green.500" },
  pending: { icon: FaRegClock, color: "blue.400" },
  warn: { icon: FaExclamationTriangle, color: "orange.400" },
  behind: { icon: FaExclamationTriangle, color: "orange.400" }, // delivered this cycle, but older than the newest supplier on the feed (2026-10-20)
  stale: { icon: FaTimesCircle, color: "red.500" },
  fail: { icon: FaTimesCircle, color: "red.500" },
  none: { icon: FaMinusCircle, color: "gray.400" },
};

// The two modes measure different things, so they must not share wording.
// 'edi' (PNP) reads a real per-file import log. 'tables' (CHK/CLI/DIS/MAK) has no
// log, so it reports whether the retailer's latest scheduled run delivered —
// which is why green here must never be read as "recent enough".
const STATE_LABELS = {
  edi: {
    ok: "Updated",
    warn: "Getting late",
    stale: "Overdue",
    fail: "Last file failed",
    none: "No data",
  },
  tables: {
    ok: "Delivered",
    behind: "Behind the latest",
    pending: "Awaiting this run",
    warn: "Missed this run",
    stale: "Missed 2+ runs",
    none: "No data",
  },
};

const LEGEND_ORDER = {
  edi: ["ok", "warn", "stale", "none"],
  tables: ["ok", "behind", "pending", "warn", "stale", "none"],
};

// Rows grouped by who maintains them (Helena 2026-10-20: "do all my suppliers, then Reworks & Other together, then Gateway,
// then Blue Mango — easier to read"), alphabetical inside each group.
const GROUPS = [
  { key: "spear", label: "Spear", orgs: ["SPEARMGMT"], color: "red.600" },
  { key: "reworks", label: "Reworks & Other", orgs: ["REWORKS", "OTHER"], color: "red.600" },
  { key: "gateway", label: "Gateway", orgs: ["GATEWAY"], color: "blue.600" },
  { key: "bluemango", label: "Blue Mango", orgs: ["BLUE_MANGO"], color: "gray.800" },
];
const groupOf = (row) => GROUPS.find((g) => g.orgs.includes(row.org)) || GROUPS[1];
const groupRows = (rows) => GROUPS.map((g) => ({ ...g, rows: rows.filter((r) => groupOf(r).key === g.key) })).filter((g) => g.rows.length);

const stateLabel = (state, mode) => STATE_LABELS[mode === "edi" ? "edi" : "tables"][state] || state;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-07-09" or "2026-07-09 06:12" -> "09 Jul"
const shortDate = (str) => {
  if (!str) return "";
  const [d] = str.split(" ");
  const parts = d.split("-");
  if (parts.length !== 3) return str;
  return `${parts[2]} ${MONTHS[parseInt(parts[1], 10) - 1]}`;
};

const StatusCell = ({ cell, mode }) => {
  if (!cell) return <Td textAlign="center">—</Td>;
  const meta = STATE_META[cell.state] || STATE_META.none;
  const label = stateLabel(cell.state, mode);

  let subtext = "";
  let tip = label;
  if (mode === "edi") {
    if (cell.state === "ok" && cell.receivedToday) {
      subtext = `${cell.rowsToday} rows`;
      tip = `File processed today ${cell.lastFile?.split(" ")[1] || ""} — ${cell.rowsToday} rows`;
    } else if (cell.state === "fail") {
      subtext = shortDate(cell.lastAttempt);
      tip = `Most recent file FAILED (${cell.lastAttempt}). Last success: ${cell.lastFile || "none"}`;
    } else if (cell.lastFile) {
      subtext = shortDate(cell.lastFile);
      tip = `Last file received ${cell.lastFile}`;
    } else {
      subtext = "";
      tip = "No files in the last 60 days";
    }
  } else {
    // Tables mode: the question is "did the run we expect have landed actually
    // deliver for this supplier", so the expected run date belongs in the tooltip.
    const expected = cell.cycleExpected ? shortDate(cell.cycleExpected) : null;
    if (cell.lastImport || cell.latestData) {
      subtext = shortDate(cell.shownDate || cell.lastImport || cell.latestData);
      const bits = [];
      if (expected) bits.push(cell.state === "ok" ? `${expected} run delivered` : `nothing from the ${expected} run`);
      if (cell.lastImport) bits.push(`last file imported ${cell.lastImport}`);
      if (cell.latestData) bits.push(`data to ${cell.latestData}`);
      if (cell.state === "behind" && cell.latestOnFeed) bits.push(`latest on this feed ${cell.latestOnFeed}`);
      tip = `${label} — ${bits.join(" · ")}`;
    } else {
      tip = `${label} — no data in the last 6 months`;
    }
  }

  return (
    <Td textAlign="center" py={1.5}>
      <Tooltip label={tip} fontSize="xs" hasArrow>
        <Box display="inline-block">
          <Icon as={meta.icon} color={meta.color} boxSize={4} display="block" mx="auto" />
          {subtext && (
            <Text fontSize="10px" color="gray.500" lineHeight="1.2">
              {subtext}
            </Text>
          )}
        </Box>
      </Tooltip>
    </Td>
  );
};

const DataStatus = () => {
  const { selectedRetailer } = useSupplierRetailer();
  const [isAdmin, setIsAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const userid = localStorage.getItem("userid");
    // Masters, plus CAMs and org admins (Blue Mango, 2026-10-02) granted the
    // DataStatus capability — the server gate (requireAdminScoped) is the real
    // control; an un-granted account opening the URL directly just gets the
    // API's 403 rendered as the error state.
    const type = localStorage.getItem("user_type");
    if (userid === "1" || userid === "2" || type === "CAM" || type === "org_admin") setIsAdmin(true);
  }, []);

  const fetchStatus = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/data/import-status`,
          { retailer: selectedRetailer, userid: localStorage.getItem("userid") },
          { signal },
        );
        if (res.data.status === 1) setData(res.data.data);
        else setError(res.data.message || "Failed to load import status");
      } catch (e) {
        if (!axios.isCancel(e)) setError(e?.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    },
    [selectedRetailer],
  );

  useEffect(() => {
    if (!isAdmin || !selectedRetailer) return;
    const controller = new AbortController();
    fetchStatus(controller.signal);
    return () => controller.abort();
  }, [isAdmin, selectedRetailer, fetchStatus]);

  if (!isAdmin) {
    return (
      <Box mt={16} ml={8}>
        <Text color="gray.500">This page is only available to administrators.</Text>
      </Box>
    );
  }

  return (
    <Box mt={16} px={8} maxW="900px">
      <Flex align="center" justify="space-between" mb={1}>
        <Flex align="center" gap={3}>
          <Heading size="md">Data Status</Heading>
          {data && (
            <Badge colorScheme="teal" fontSize="sm">
              {data.requestedRetailer || data.retailer}
            </Badge>
          )}
          {data?.sharedFeed && (
            <Badge colorScheme="gray" fontSize="xs">
              shares the Checkers feed
            </Badge>
          )}
        </Flex>
        <Flex align="center" gap={3}>
          {data && (
            <Text fontSize="sm" color="gray.500">
              Today: {data.today}
            </Text>
          )}
          <Button size="sm" leftIcon={<FaSyncAlt />} onClick={() => fetchStatus()} isLoading={loading}>
            Refresh
          </Button>
        </Flex>
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={4}>
        {data?.mode === "edi"
          ? "Green = a file for that feed was processed successfully today (Strike Rate: within 7 days)."
          : `Green = the ${data?.cycle?.current ? shortDate(data.cycle.current) : "latest"} ${
              data?.cadence === "monthly" ? "monthly" : "weekly"
            } run delivered a file AND the supplier is on the newest date this feed has. Amber = delivered but behind the newest date, or the run was missed. Blue = it hasn't delivered yet. Red = two or more runs missed. Suppliers no longer on file, or not on this retailer with nothing delivered in 45 days, are not listed.`}
      </Text>

      <Text fontSize="xs" color="gray.500" mt={-3} mb={4}>
        Grouped by who maintains the data: <Text as="span" color="red.600" fontWeight="semibold">Spear</Text> (Helena), Reworks &amp; Other, Gateway, Blue Mango (Linda). Every retailer tab above is open on this page.
      </Text>

      {error && (
        <Text color="red.500" mb={4}>
          {error}
        </Text>
      )}

      {loading && !data ? (
        <Spinner />
      ) : (
        data && (
          <>
            <Table size="sm" border="1px" borderColor="gray.200" borderRadius="md">
              <Thead>
                <Tr>
                  <Th>Supplier</Th>
                  {data.columns.map((c) => (
                    <Th key={c} textAlign="center">
                      {COLUMN_LABELS[c] || c}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {groupRows(data.rows).map((g) => (
                  <React.Fragment key={g.key}>
                    <Tr bg="gray.50">
                      <Td colSpan={data.columns.length + 1} py={1} fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color="red.600">
                        {g.label} · {g.rows.length}
                      </Td>
                    </Tr>
                    {g.rows.map((row) => (
                      <Tr key={row.supplier}>
                        {/* names in black, the group heading in red says whose they are (Helena 2026-10-20) */}
                        <Td py={1.5} fontSize="sm" fontWeight="medium" color="gray.800">
                          {row.supplier}
                        </Td>
                        {data.columns.map((c) => (
                          <StatusCell key={c} cell={row.cells[c]} mode={data.mode} />
                        ))}
                      </Tr>
                    ))}
                  </React.Fragment>
                ))}
                {data.rows.length === 0 && (
                  <Tr>
                    <Td colSpan={data.columns.length + 1}>
                      <Text color="gray.500">No suppliers linked to this retailer.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>

            <Flex mt={3} gap={5} wrap="wrap" fontSize="xs" color="gray.600">
              {(LEGEND_ORDER[data.mode === "edi" ? "edi" : "tables"] || []).map((k) => (
                <Flex key={k} align="center" gap={1}>
                  <Icon as={STATE_META[k].icon} color={STATE_META[k].color} boxSize={3} />
                  <Text>{stateLabel(k, data.mode)}</Text>
                </Flex>
              ))}
            </Flex>
          </>
        )
      )}
    </Box>
  );
};

export default DataStatus;
